<template>
	<v-card elevation="3">
		<v-form>
			<v-card-title primary-title>
				{{ $T('title') }}
				<v-spacer></v-spacer>
				<v-btn
					@click="$emit('close'); clear();"
					icon>
					<v-icon color="alert-color">mdi-close</v-icon>
				</v-btn>
			</v-card-title>
			<v-card-text>
				<v-row dense>
					<!-- employee -->
					<v-col cols="12">
						<v-autocomplete
							:disabled="!!oneEmployee || !employees.length"
							v-model="employee"
							:items="employees"
							:loading="!employees.length"
							return-object
							item-value="employeeId"
							:label="$T('employee')"
							:filter="
								(item, queryText) =>
									item.code?.includes(queryText) || item.arabicFullName.includes(queryText)
							"
							dense
							outlined
							hide-details>
							<template v-slot:item="{ item }">
								<div
									class="d-flex justify-space-between text-caption"
									style="width: 100%">
									<div>
										({{ item.code }})
										{{ item.arabicFullName }}
									</div>
									<div>
										{{ item.sectionName }}
									</div>
								</div>
							</template>
							<template v-slot:selection="{ item }">
								<div
									class="d-flex justify-space-between"
									style="min-width: 100%">
									<div>
										({{ item.code }})
										{{ item.arabicFullName }}
									</div>
									<div>
										{{ item.sectionName }}
									</div>
								</div>
							</template>
						</v-autocomplete>
					</v-col>
					<!-- timeType -->
					<v-col cols="12">
						<v-radio-group
							v-model="addMission.timeType"
							dense
							row
							hide-details
							mandatory
							class="mt-1">
							<v-radio
								:label="$T('addMission.forDay')"
								:value="0"></v-radio>
							<v-radio
								:label="$T('addMission.forHour')"
								:value="1"></v-radio>
						</v-radio-group>
					</v-col>
					<!-- date -->
					<v-col :cols="addMission.timeType === timeTypeEnum.hourly ? 5 : 6">
						<bee-date-picker
							v-model="addMission.date"
							:input-label="$T('inputs.date')"
							:input-props="{ outlined: true, dense: true, prependInnerIcon: '$calendar' }"
							:months-over-max="100"
							menu-picker
							calendar
							no-title
							hide-details></bee-date-picker>
					</v-col>
					<!-- time -->
					<v-col
						cols="4"
						v-if="addMission.timeType === timeTypeEnum.hourly">
						<v-text-field
							v-model="addMission.time"
							type="time"
							outlined
							dense
							:label="$T('addMission.time')"
							hide-details></v-text-field>
					</v-col>
					<!-- duration -->
					<v-col :cols="addMission.timeType === timeTypeEnum.hourly ? 3 : 6">
						<v-text-field
							v-model="addMission.duration"
							outlined
							dense
							:label="$T('addMission.duration')"
							hide-details></v-text-field>
					</v-col>
					<!-- type -->
					<v-col cols="12">
						<b-async-input
							ref="types"
							:storeName="'filters/fetchMissionTypes'">
							<template v-slot:default="{ items, loading, focus }">
								<v-select
									v-model="addMission.type"
									outlined
									dense
									:items="items"
									:loading="loading"
									item-text="name"
									item-value="id"
									menu-props="offset-y"
									:label="$T('addMission.type')"
									hide-details
									@focus="focus">
									<template v-slot:no-data>
										<span>
											<v-progress-linear
												v-if="loading"
												indeterminate></v-progress-linear>
										</span>
									</template>
								</v-select>
							</template>
						</b-async-input>
					</v-col>
					<!-- cause -->
					<v-col :cols="12">
						<v-text-field
							v-model="addMission.cause"
							outlined
							dense
							:label="$T('addMission.cause')"
							hide-details></v-text-field>
					</v-col>
				</v-row>
				<div
					class="text-body-1 mt-3"
					v-if="
						addMission.date &&
						addMission.duration &&
						(addMission.timeType === timeTypeEnum.daily || addMission.time)
					">
					تسجيل طلب إجازة
					<br />
					في تاريخ
					<strong> {{ moment(this.addMission.date).format('L') }} </strong>
					<br />
					<template v-if="addMission.timeType === timeTypeEnum.daily">
						وحتى تاريخ
						<strong>
							{{ moment(this.addMission.date).add(this.addMission.duration, 'd').format('L') }}
						</strong>
					</template>
					<template v-if="addMission.timeType === timeTypeEnum.hourly">
						من الساعة
						<strong>
							{{ momentInstance(this.addMission.time, 'hh:mm') .format('hh:mm a') }}
						</strong>
						وحتى الساعة
						<strong>
							{{
								momentInstance(this.addMission.time, 'hh:mm')
									.add(this.addMission.duration, 'm')
									.format('hh:mm a')
							}}
						</strong>
					</template>
				</div>
			</v-card-text>
			<v-card-actions class="align-center">
				<v-spacer></v-spacer>
				<v-btn
					:loading="loadingAdding"
					:disabled="loadingAdding || cantAdd"
					color="secondary"
					@click="addMissionFun">
					{{ $T('default-buttons.save') }}
				</v-btn>
			</v-card-actions>
		</v-form>
	</v-card>
</template>

<script>
import { mapState } from 'vuex';
import moment, { momentInstance } from '@/utils/moment';
import { nextTick } from 'vue';

const timeTypeEnum = {
	daily: 0,
	hourly: 1,
};

export default {
	name: 'AddMissionComponent',

	props: {
		oneEmployee: { type: String },
		selectedItem: { type: Object },
	},

	data() {
		return {
			loadingAdding: false,

			employee: null,

			addMission: {
				type: null,
				timeType: timeTypeEnum.daily,
				data: new Date().toISOString().slice(0, 10),
				time: null,
				isPaid: false,
				cause: null,
				duration: null,
			},

			timeTypeEnum,
		};
	},

	watch: {
		'addMission.timeType'() {
			this.addMission.time = null;
			this.addMission.type = null;
		},
		oneEmployee() {
			this.handelEmployee();
		},
		selectedItem() {
			this.handelData();
		},
	},

	computed: {
		cantAdd() {
			if (this.addMission.timeType === timeTypeEnum.daily) {
				return (
					!this.employee ||
					!this.addMission.type ||
					!this.addMission.date ||
					!this.addMission.duration
				);
			} else {
				return !this.employee || !this.addMission.time || !this.addMission.duration;
			}
		},
		...mapState({
			employees: (state) => state.employee.employees,
		}),
	},

	methods: {
		addMissionFun() {
			if (this.cantAdd) return;
			this.loadingAdding = true;

			this.$store
				.dispatch('attendance/addMission', {
					id: this.addMission.id,
					employeeId: this.employee.employeeId,
					mission: this.addMission,
				})
				.then(() => {
					this.$emit('close');
					this.$emit('done');
					this.clear();
					this.$eventBus.$emit('show-snackbar', this.$T('messages.add-done'), {
						done: true,
					});
				})
				.catch((err) => {
					err.default && err.default(this.$T('messages.add-fail'), false);
				})
				.finally(() => {
					this.loadingAdding = false;
				});
		},

		handelEmployee() {
			this.employee = null;
			if (this.oneEmployee)
				this.employee = this.employees.find((e) => e.employeeId === this.oneEmployee);
		},
		handelData() {
			this.clear();
			if (this.selectedItem) {
				this.loadingAdding = true;
				this.$store.dispatch('filters/fetchMissionTypes').then((res) => {
					this.$refs.types.addItems(res);

					// info: this used for time input
					this.addMission.timeType = this.selectedItem.isDaily
						? timeTypeEnum.daily
						: timeTypeEnum.hourly;

					nextTick(() => {
						this.addMission = {
							id: this.selectedItem.id,
							type: this.selectedItem.taskTypeId,
							timeType: this.selectedItem.isDaily ? timeTypeEnum.daily : timeTypeEnum.hourly,
							date: moment(this.selectedItem.date).format('L'),
							time: moment(this.selectedItem.startTime).format('HH:mm'),
							isPaid: this.selectedItem.withSalary,
							cause: this.selectedItem.reason,
							duration: this.selectedItem.durationInDay || this.selectedItem.durationInMinute,
						};
					});
					this.loadingAdding = false;
				});
			}
		},
		clear() {
			this.addMission = {
				employee: null,
				type: null,
				timeType: null,
				data: new Date().toISOString().slice(0, 10),
				time: null,
				isPaid: false,
				cause: null,
				duration: null,
			};
		},
		moment,
		momentInstance,
	},

	mounted() {
		this.handelEmployee();
		this.handelData();
	},
};
</script>
